const DEFAULT_DISPLAY_MS = 15_000;
const DEFAULT_INTERMISSION_MS = 400;
const DEFAULT_POLL_INTERVAL_MS = 8_000;

function readLastSeenId(windowRef) {
  try {
    const stored = windowRef?.localStorage?.getItem(
      "glitchlings:lastAchievementId",
    );
    const parsed = Number(stored || "0");
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  } catch (error) {
    console.warn("Achievement notification cache read failed:", error);
    return 0;
  }
}

function persistLastSeenId(windowRef, value) {
  try {
    windowRef?.localStorage?.setItem(
      "glitchlings:lastAchievementId",
      String(value),
    );
  } catch (error) {
    console.warn("Achievement notification cache write failed:", error);
  }
}

function formatReward(reward) {
  if (!reward) {
    return "";
  }

  const normalized =
    typeof reward === "object" && reward.type
      ? reward
      : {
          type: reward.type || reward.rewardType,
          value: reward.value || reward.rewardValue,
        };

  if (!normalized || !normalized.type) {
    return "";
  }

  if (normalized.type === "coins") {
    const raw =
      typeof normalized.value === "object"
        ? normalized.value?.coins
        : normalized.value;
    if (Number.isFinite(raw)) {
      return `💰 +${Number(raw).toLocaleString("ru-RU")} монет`;
    }
  }

  if (normalized.type === "stars") {
    const raw =
      typeof normalized.value === "object"
        ? normalized.value?.stars
        : normalized.value;
    if (Number.isFinite(raw)) {
      return `⭐ +${Number(raw).toLocaleString("ru-RU")} звёзд`;
    }
  }

  return typeof normalized.value === "string" ? normalized.value : "";
}

export function createAchievementNotificationManager({
  windowRef = window,
  documentRef = document,
  fetchFn,
  getAuthHeaders,
  getApiUrl,
  toastSelector = "#achievement-toast",
  displayDurationMs = DEFAULT_DISPLAY_MS,
  dismissIntermissionMs = DEFAULT_INTERMISSION_MS,
  defaultPollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
} = {}) {
  const state = {
    queue: [],
    active: null,
    timer: null,
    pollTimer: null,
    isPolling: false,
    lastSeenId: readLastSeenId(windowRef),
    displayedIds: new Set(),
    initialized: false,
  };

  function enqueueNotifications(items = []) {
    if (!Array.isArray(items) || items.length === 0) {
      return;
    }

    const queued = [];

    for (const item of items) {
      if (!item) continue;
      const notificationId = Number(
        item.id ??
          item.userAchievementId ??
          item.achievementId ??
          item.user_achievement_id ??
          0,
      );
      if (notificationId && state.displayedIds.has(notificationId)) {
        continue;
      }

      const meta = item.achievement || item;
      const reward = item.reward || {
        type: item.rewardType,
        value: item.rewardValue,
      };

      queued.push({
        id: notificationId || null,
        icon: meta.icon || item.icon || "🏆",
        title: meta.name || item.name || "Новое достижение",
        description: meta.description || item.description || "",
        rewardText: formatReward(reward),
        unlockedAt: item.unlockedAt || item.unlocked_at || null,
        raw: item,
      });

      if (notificationId) {
        state.displayedIds.add(notificationId);
        if (notificationId > state.lastSeenId) {
          state.lastSeenId = notificationId;
        }
      }
    }

    if (queued.length === 0) {
      return;
    }

    if (state.displayedIds.size > 200) {
      const excess = state.displayedIds.size - 200;
      const iterator = state.displayedIds.values();
      for (let index = 0; index < excess; index += 1) {
        const next = iterator.next();
        if (next.done) {
          break;
        }
        state.displayedIds.delete(next.value);
      }
    }

    if (state.lastSeenId > 0) {
      persistLastSeenId(windowRef, state.lastSeenId);
    }

    state.queue.push(...queued);

    if (!state.active) {
      showNextNotification();
    }
  }

  function showNextNotification() {
    if (state.active || state.queue.length === 0) {
      return;
    }

    const next = state.queue.shift();
    if (!next) {
      return;
    }

    state.active = next;

    const toast = documentRef.querySelector(toastSelector);
    if (!toast) {
      state.active = null;
      return;
    }

    const iconNode = toast.querySelector("[data-achievement-icon]");
    if (iconNode) {
      iconNode.textContent = next.icon || "🏆";
    }

    const titleNode = toast.querySelector("[data-achievement-title]");
    if (titleNode) {
      titleNode.textContent = next.title || "Новое достижение";
    }

    const descriptionNode = toast.querySelector(
      "[data-achievement-description]",
    );
    if (descriptionNode) {
      descriptionNode.textContent = next.description || "";
      descriptionNode.style.display = next.description ? "block" : "none";
    }

    const rewardNode = toast.querySelector("[data-achievement-reward]");
    if (rewardNode) {
      rewardNode.textContent = next.rewardText || "";
      rewardNode.style.display = next.rewardText ? "block" : "none";
    }

    toast.classList.remove("hidden");
    toast.classList.add("active");

    if (state.timer) {
      windowRef.clearTimeout(state.timer);
    }

    state.timer = windowRef.setTimeout(() => {
      dismissNotification({ auto: true });
    }, displayDurationMs);
  }

  function dismissNotification({ auto = false } = {}) {
    if (state.timer) {
      windowRef.clearTimeout(state.timer);
      state.timer = null;
    }

    const toast = documentRef.querySelector(toastSelector);
    if (toast) {
      toast.classList.remove("active");
      toast.classList.add("hidden");
    }

    state.active = null;

    if (state.queue.length > 0) {
      windowRef.setTimeout(() => {
        showNextNotification();
      }, dismissIntermissionMs);
    }
  }

  function processPayload(payload) {
    if (!payload || typeof payload !== "object") {
      return;
    }
    if (
      Array.isArray(payload.achievementNotifications) &&
      payload.achievementNotifications.length > 0
    ) {
      enqueueNotifications(payload.achievementNotifications);
    }
  }

  function schedulePoll(delay = defaultPollIntervalMs) {
    if (state.pollTimer) {
      windowRef.clearTimeout(state.pollTimer);
    }

    state.pollTimer = windowRef.setTimeout(async () => {
      try {
        await fetchFeed();
      } catch (error) {
        console.error("Achievement feed poll failed:", error);
        schedulePoll(delay * 2);
      }
    }, delay);
  }

  async function fetchFeed() {
    if (typeof fetchFn !== "function") {
      schedulePoll();
      return;
    }

    const params = new URLSearchParams();
    params.set("after", String(state.lastSeenId));
    params.set("limit", "25");

    const apiUrl = typeof getApiUrl === "function" ? getApiUrl() : getApiUrl;
    const url = `${apiUrl}/achievements/feed?${params.toString()}`;

    const result = await fetchFn(url, {
      headers:
        typeof getAuthHeaders === "function"
          ? getAuthHeaders()
          : getAuthHeaders,
    });

    if (!result.success) {
      schedulePoll();
      return;
    }

    const data = result.data || {};
    const achievements = Array.isArray(data.achievements)
      ? data.achievements
      : [];

    if (achievements.length > 0) {
      state.initialized = true;
      enqueueNotifications(achievements);

      const maxId = achievements.reduce((max, item) => {
        const id = Number(
          item.id ??
            item.userAchievementId ??
            item.achievementId ??
            item.user_achievement_id ??
            0,
        );
        return id > max ? id : max;
      }, state.lastSeenId);

      if (maxId > state.lastSeenId) {
        state.lastSeenId = maxId;
        if (state.lastSeenId > 0) {
          persistLastSeenId(windowRef, state.lastSeenId);
        }
      }
    } else if (!state.initialized) {
      state.initialized = true;
    }

    schedulePoll();
  }

  function ensurePolling() {
    if (state.isPolling) {
      return;
    }
    state.isPolling = true;
    fetchFeed().catch((error) => {
      console.error("Achievement feed initial poll failed:", error);
      schedulePoll();
    });
  }

  return {
    state,
    enqueueNotifications,
    dismissNotification,
    processPayload,
    schedulePoll,
    fetchFeed,
    ensurePolling,
  };
}
